import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, MapPin, Home, Users, Briefcase, Calculator, Camera, DollarSign } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const resourceLinks = [
  { path: '/trail-maps', label: 'Trail Maps', icon: MapPin, description: 'Explore 45+ km of trails' },
  { path: '/housing-market', label: 'Housing Market', icon: Home, description: 'Real estate data & pricing' },
  { path: '/community-guide', label: 'Community Guide', icon: Users, description: 'Education, healthcare & more' },
  { path: '/business-resources', label: 'Business Resources', icon: Briefcase, description: 'Incentives & support' },
  { path: '/explore', label: 'Explore Cornwall', icon: Camera, description: 'Photo galleries & videos' },
  { path: '/cost-calculator', label: 'Cost Calculator', icon: Calculator, description: 'Calculate your savings' },
  { path: '/salary-comparison', label: 'Salary Comparison', icon: DollarSign, description: 'QC vs ON take-home pay' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const resourcesRef = useRef(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isResourcePage = resourceLinks.some(link => location.pathname === link.path);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = '/#' + sectionId;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">C</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Move to Cornwall</h1>
              <p className="text-xs text-slate-600 leading-none">Ontario's Hidden Gem</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link to="/">
              <Button
                variant="ghost"
                className={`text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors ${
                  isActive('/') ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Home
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={() => scrollToSection('benefits')}
              className="text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
            >
              Why Cornwall
            </Button>
            
            {/* Resources Dropdown */}
            <div className="relative" ref={resourcesRef}>
              <Button
                variant="ghost"
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className={`text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors flex items-center gap-1 ${
                  isResourcePage ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
                data-testid="resources-dropdown-btn"
              >
                Resources
                <ChevronDown className={`w-4 h-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {resourcesOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50">
                  {resourceLinks.map((link) => {
                    const IconComponent = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setResourcesOpen(false)}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors ${
                          isActive(link.path) ? 'bg-emerald-50' : ''
                        }`}
                        data-testid={`resource-link-${link.path.slice(1)}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isActive(link.path) ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600'
                        }`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div>
                          <div className={`font-medium ${isActive(link.path) ? 'text-emerald-600' : 'text-slate-900'}`}>
                            {link.label}
                          </div>
                          <div className="text-xs text-slate-500">{link.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <Link to="/quizzes">
              <Button
                variant="ghost"
                className={`text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors ${
                  isActive('/quizzes') ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Quizzes
              </Button>
            </Link>
            <Link to="/advertising">
              <Button
                variant="ghost"
                className={`text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 transition-colors ${
                  isActive('/advertising') ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Advertise
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                className={`ml-4 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-md hover:shadow-lg ${
                  isActive('/contact') ? 'bg-emerald-700' : ''
                }`}
              >
                Contact
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <nav className="px-4 py-4 space-y-2">
            <Link to="/" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant="ghost"
                className={`w-full justify-start text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 ${
                  isActive('/') ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Home
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={() => scrollToSection('benefits')}
              className="w-full justify-start text-slate-700 hover:text-emerald-600 hover:bg-emerald-50"
            >
              Why Cornwall
            </Button>
            
            {/* Mobile Resources Accordion */}
            <div>
              <Button
                variant="ghost"
                onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                className={`w-full justify-between text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 ${
                  isResourcePage ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Resources
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileResourcesOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {mobileResourcesOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-emerald-200 pl-4">
                  {resourceLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileResourcesOpen(false);
                      }}
                    >
                      <Button
                        variant="ghost"
                        className={`w-full justify-start text-sm text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 ${
                          isActive(link.path) ? 'text-emerald-600 bg-emerald-50' : ''
                        }`}
                      >
                        {link.label}
                      </Button>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/quizzes" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant="ghost"
                className={`w-full justify-start text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 ${
                  isActive('/quizzes') ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Quizzes
              </Button>
            </Link>
            <Link to="/advertising" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant="ghost"
                className={`w-full justify-start text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 ${
                  isActive('/advertising') ? 'text-emerald-600 bg-emerald-50' : ''
                }`}
              >
                Advertise
              </Button>
            </Link>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <Button
                className={`w-full bg-emerald-600 hover:bg-emerald-700 text-white ${
                  isActive('/contact') ? 'bg-emerald-700' : ''
                }`}
              >
                Contact
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
